"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Shield, Globe2, Layers3, Users, Wallet, Workflow, Trophy, TrendingUp } from "lucide-react";

import HeroWaveBackground from "./HeroWaveBackground";
import MetaballHeroBackground from "./MetaballHeroBackground";
import founderPhoto from "../images/Mainpage/hero-founder-cutout.png";
import teamPhoto from "../images/Mainpage/hero-team-cutout.png";
import founderSignature from "../images/Mainpage/hero-founder-signature.png";
import awardTrophy from "../images/Mainpage/hero-award-trophy.png";
import BankingPartnersSlider from "./BankingPartnersSLider";

// Confetti pieces for the award-showcase slide — fixed set so it doesn't re-randomize (and
// re-jump) on every render, just re-plays from the top each time that slide becomes active.
const CONFETTI_PIECES: { left: string; color: string; delay: string; duration: string }[] = [
  { left: "12%", color: "#195AFF", delay: "0s", duration: "2.2s" },
  { left: "26%", color: "#53F6FF", delay: "0.3s", duration: "2.6s" },
  { left: "40%", color: "#FFC93C", delay: "0.6s", duration: "2.1s" },
  { left: "58%", color: "#195AFF", delay: "0.15s", duration: "2.4s" },
  { left: "70%", color: "#53F6FF", delay: "0.5s", duration: "2.3s" },
  { left: "84%", color: "#FFC93C", delay: "0.35s", duration: "2.5s" },
  { left: "20%", color: "#FFC93C", delay: "0.8s", duration: "2.2s" },
  { left: "64%", color: "#195AFF", delay: "0.9s", duration: "2.6s" },
];

const capabilities = [
  { icon: Globe2, title: "Your website", detail: "Built to convert", color: "bg-blue-50 text-blue-600" },
  { icon: Users, title: "Your leads", detail: "Connected to your CRM", color: "bg-violet-50 text-violet-600" },
  { icon: Workflow, title: "Automation", detail: "Work smarter", color: "bg-amber-50 text-amber-600" },
  { icon: Wallet, title: "Your payments", detail: "Seamlessly integrated", color: "bg-emerald-50 text-emerald-700" },
];

// Two rotating hero content sets. The heading and subtitle type independently/in parallel
// (each on its own timer) rather than the subtitle waiting for the heading to finish — they
// just both hold once fully typed, then both delete together before the next set starts.
const HERO_TEXT_SLIDES = [
  {
    headingLines: [
      "GrowthOS: Where Growth Has an",
      "Operating System for Websites, Leads & Sales 24x7",
    ] as [string, string],
    subtitle:
      "Fintech, EdTech, or anything in between ? GrowthOS gives your business the technology, marketing, and operating systems to grow at every stage with confidence, without hiring five different vendors to do it.",
  },
  {
    headingLines: [
      "Grow Your Business Faster with an",
      "AI-Powered Website That Gets You Leads & Sales 24x7",
    ] as [string, string],
    subtitle:
      "We equip you with high-converting Business websites, CRM automation, and fintech tools that make your business stronger, faster, and future-ready.",
  },
];

const SLIDE_HOLD_MS = 8000;
// Three visual states in the photo column — founder photo (0), team photo (1), then a
// non-photo "award showcase" panel (2) — but only two real heading/subtitle copies exist, so
// the heading just repeats slide 0's copy for that third visual state (see headingSlide below).
const TOTAL_SLIDES = 3;

// Cycles through the 3 visual states on a timer. No typing effect — the heading and subtitle
// just scale/fade in as whole blocks (see ScaleInHeading/ScaleInSubtitle below) each time the
// slide changes.
function useHeroSlide() {
  const [slideIndex, setSlideIndex] = useState(0);
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    setReducedMotion(window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  }, []);

  useEffect(() => {
    if (reducedMotion) return;
    const interval = setInterval(() => {
      setSlideIndex((i) => (i + 1) % TOTAL_SLIDES);
    }, SLIDE_HOLD_MS);
    return () => clearInterval(interval);
  }, [reducedMotion]);

  return { slide: HERO_TEXT_SLIDES[slideIndex % HERO_TEXT_SLIDES.length], slideIndex, reducedMotion };
}

// Heading now uses the exact same fade/slide animation as the subtitle (see ScaleInSubtitle
// below) instead of the old per-character scale-in effect, so both pieces of hero copy animate
// the same way when the slide changes.
const ScaleInHeading = ({ line1, line2, slideIndex }: { line1: string; line2: string; slideIndex: number }) => (
  <AnimatePresence mode="wait">
    <motion.span
      key={slideIndex}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      className="inline-block font-NeueMontreal "
    >
      {line1}
      <br />
      <span className="text-[#195AFF] font-NeueMontreal ">{line2}</span>
    </motion.span>
  </AnimatePresence>
);

const ScaleInSubtitle = ({ text, slideIndex }: { text: string; slideIndex: number }) => (
  <AnimatePresence mode="wait">
    <motion.span
      key={slideIndex}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      className="inline-block font-Schibsted_Grotesk"
    >
      {text}
    </motion.span>
  </AnimatePresence>
);

export default function HomeHero() {
  const { slide, slideIndex, reducedMotion } = useHeroSlide();
  const [line1, line2] = slide.headingLines;

  return (
    <section aria-labelledby="home-hero-title" className="bg-[#F7F9FF] text-[#090D28]">
      {/* Animated backgrounds are scoped to this wrapper only — not the Banking Partners strip
          below — so that section keeps sitting on the plain page background instead of the
          busy animated one, which was making its "Banking partners" label unreadable. */}
      <div className="relative isolate overflow-hidden">
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_85%_20%,#DBE6FF_0%,transparent_55%)]" />
        {/* <HeroWaveBackground /> */}
          <MetaballHeroBackground/>

        <div className="relative mx-auto max-w-[1520px] px-5  sm:px-8   lg:px-12 ">
          {/* This was lg:grid-cols (1024px) — too narrow for how much room the heading/CTAs
              actually need. At in-between widths (e.g. a desktop window with DevTools docked,
              or a smaller laptop) it forced two columns without enough space and the text
              overflowed past the edge. Pushed to xl (1280px) so it stays single-column,
              stacked, until there's genuinely enough width for two. */}
          <div className="grid items-center gap-0 xl:grid-cols-[1.1fr_1fr] xl:gap-14">
          <div className="min-w-0 h-[60vh] sm:h-fit">
            <div className="mb-7 inline-flex items-center gap-2.5 rounded-full border border-[#195AFF]/15 bg-white px-4 py-2.5 text-[#536489]">
              <Shield aria-hidden="true" className="h-4 w-4" />
              <span className="font-Schibsted_Grotesk text-[9px] font-bold uppercase leading-tight tracking-wider">Powered by<br />GrowthOS</span>
            </div>
            <h1 id="home-hero-title" className="m-0 min-h-[2.2em] font-NeueMontreal text-[38px] font-bold leading-[1.1] tracking-[-1px] sm:min-h-[2.2em] sm:text-[48px] xl:text-[56px]">
              {reducedMotion ? (
                <>
                  {line1}
                  <br />
                  <span className="text-[#195AFF]">{line2}</span>
                </>
              ) : (
                <ScaleInHeading line1={line1} line2={line2} slideIndex={slideIndex} />
              )}
            </h1>
            <p className="mb-0 mt-6 max-w-[560px] min-h-[5.25em] font-Schibsted_Grotesk text-base leading-[1.75] text-[#596278] md:min-h-[3.5em] md:text-lg">
              {reducedMotion ? slide.subtitle : <ScaleInSubtitle text={slide.subtitle} slideIndex={slideIndex} />}
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              {/* Hidden until the layout is actually two-column (xl) — re-appears below the
                  founder photo section before that (see the xl:hidden copy further down) so it
                  doesn't sit above the photo there. */}
              <Link href="/book-demo" prefetch={false} className="hidden min-h-14 items-center justify-center gap-3 rounded-full bg-[#195AFF] px-7 font-Schibsted_Grotesk text-sm font-semibold text-white transition-colors hover:bg-[#3a6ceb] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-blue-600 xl:inline-flex">
                Book a Free Demo <ArrowRight aria-hidden="true" className="h-4 w-4" />
              </Link>
              <Link href="#core-ecosystem" prefetch={false} className="hidden xl:inline-flex min-h-14 items-center justify-center rounded-full border border-[#D9DFEB] bg-white px-7 font-Schibsted_Grotesk text-sm font-semibold text-[#172342] transition-colors hover:bg-blue-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-blue-600">
                Explore GrowthOS
              </Link>
            </div>
          </div>



          <div className="relative min-w-0 pt-2">
             {/* <div className="relative z-[2] mx-auto -mt-2 inline-flex w-fit items-center gap-[7px] rounded-full bg-white px-4 py-[9px] font-['Inter',Arial,sans-serif] text-xs font-bold leading-none tracking-[0.3px] text-[#172342] shadow-[0_12px_28px_-8px_rgba(9,13,40,0.3)]">
                <Trophy aria-hidden="true" className="h-3.5 w-3.5 text-[#f0b429]" />
                Best Tech &amp; Consultant 2026

                
              </div> */}
            <div className="relative ml-auto w-full max-w-[460px] px-3 pt-0 md:pt-6 xl:max-w-[520px] xl:-mr-7">
              <div className="relative z-[1] aspect-[4/5] w-full drop-shadow-[0_30px_45px_rgba(9,13,40,0.28)]">
                {/* Three visual states cycling in this box: founder photo (0), team photo (1),
                    then a non-photo "award showcase" panel (2) — see below. */}
                <Image
                  src={founderPhoto}
                  alt="Founder of Vilva Business"
                  fill
                  className={`object-contain object-bottom transition-opacity duration-500 ${slideIndex === 0 ? "opacity-100" : "opacity-0"}`}
                  priority
                  sizes="(min-width: 824px) 420px, 75vw"
                />
                <Image
                  src={teamPhoto}
                  alt="The Vilva Business team"
                  fill
                  className={` object-contain object-bottom transition-opacity duration-500 ${slideIndex === 1 ? "opacity-100" : "opacity-0"}`}
                  sizes="(min-width: 824px) 420px, 75vw"
                />
                {/* Signature, placed near the hand/crossed-arms area of the photo, tilted —
                    white ink on transparent, kept as-is (no color filter). Only shown alongside
                    the founder photo (slide 0), not the team photo. */}
                <Image
                  src={founderSignature}
                  alt=""
                  aria-hidden="true"
                  className={`pointer-events-none absolute bottom-[7%] right-[10%] z-[2] w-[70%] -rotate-12 object-contain transition-opacity duration-500 ${slideIndex === 0 ? "opacity-80" : "opacity-0"}`}
                />
                {/* Only clickable/visible alongside the founder photo (slide 0) — links to the
                    full founder profile page. */}


                {/* Award ribbon — draped diagonally over the top-right corner, shown only on
                    the trophy/award slide (2), not the photo slides. */}
                {/* <div
                  aria-hidden="true"
                  className={`pointer-events-none absolute right-[-34%] top-[9%] z-[3] w-[150%] rotate-45 bg-gradient-to-r from-[#195AFF] to-[#53F6FF] py-1.5 text-center shadow-[0_6px_16px_-4px_rgba(9,13,40,0.4)] transition-opacity duration-700 ${
                    slideIndex === 2 ? "opacity-100" : "opacity-0"
                  }`}
                >
                  <span className="font-['Inter',Arial,sans-serif] text-[11px] font-extrabold uppercase tracking-wider text-white sm:text-xs">
                    &#9733; Award-Winning &middot; 2026
                  </span>
                </div> */}

                {/* Award showcase panel — the third slide, not a photo. Light theme to match
                    the rest of the hero (was a dark navy box before, which clashed). Trophy
                    graphic with confetti pieces drifting up around it, plus the award headline
                    and a highlighted "years of excellence" stat.
                    justify-center + overflow-hidden inside this aspect-ratio-locked box meant
                    that on widths (e.g. iPad) where this content is taller than the box, the
                    centered content overflowed evenly and the bottom (the stat pill) got hard
                    clipped. Content now flows from the top and is allowed to overflow past the
                    box instead of being clipped — safe here since the pill below the photo is
                    already hidden (opacity-0) on this slide, so there's nothing for it to cover. */}
                <div
                  className={`absolute inset-x-0 top-0 flex flex-col items-center justify-start gap-4 overflow-visible px-6 pt-6 text-center transition-opacity duration-700 ${
                    slideIndex === 2 ? "opacity-100" : "pointer-events-none opacity-0"
                  }`}
                >
                  {/* Confetti pieces — staggered delay/left offset/color per piece via inline style. */}
                  {slideIndex === 2 &&
                    CONFETTI_PIECES.map((piece, i) => (
                      <span
                        key={i}
                        aria-hidden="true"
                        className="absolute bottom-[38%] h-2 w-2 rounded-sm motion-reduce:hidden animate-confetti-float"
                        style={{
                          left: piece.left,
                          backgroundColor: piece.color,
                          animationDelay: piece.delay,
                          animationDuration: piece.duration,
                        }}
                      />
                    ))}

                  <span className="inline-flex mt-0 sm:mt-7 items-center gap-1.5 rounded-full border border-[#195AFF]/15 bg-white px-3.5 py-1.5 font-Schibsted_Grotesk text-[10px] font-bold uppercase tracking-wider text-[#195AFF] shadow-sm">
                    <Trophy aria-hidden="true" className="h-3 w-3 text-[#f0b429]" />
                    Winner &amp; Industry Standard
                  </span>

                  <div className="relative ">
                    <Image
                      src={awardTrophy}
                      alt="awardTrophy"
                      aria-hidden="true"
                      className="w-[150px] sm:w-[250px] object-contain drop-shadow-[0_16px_28px_rgba(25,90,255,0.3)]"
                    />
                  </div>

                  <p className="m-0 font-['Inter',Arial,sans-serif] text-xl font-extrabold uppercase leading-tight tracking-tight text-[#172342] sm:text-2xl">
                    Award-Winning
                    <br />
                    <span className="text-[#195AFF] font-['Inter',Arial,sans-serif]">Tech Consulting</span> Company
                    <br />
                    2026
                  </p>

                  <p className="m-0 max-w-[280px] font-Schibsted_Grotesk text-xs leading-snug text-[#596278] sm:text-sm">
                    Recognized for delivering GrowthOS websites, payments, and automation that move the needle for real businesses.
                  </p>

                  <div className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 shadow-[0_8px_20px_-8px_rgba(9,13,40,0.25)] ring-1 ring-[#195AFF]/15">
                    <span className="font-['Inter',Arial,sans-serif] text-base font-extrabold text-[#195AFF]">11+</span>
                    <span className="font-Schibsted_Grotesk text-xs font-semibold uppercase tracking-wide text-[#596278]">
                      Years of Excellence
                    </span>
                  </div>
                </div>
              </div>

              {/* Below the photo now — a compact pill instead of a full card, and the award
                  message itself also lives on the ribbon over the photo corner. */}
              <div className={`relative z-[2] flex justify-center pb-2 transition-opacity duration-500 ${slideIndex === 2 ? "opacity-0" : "opacity-100"}`}>
                <div className="inline-flex items-start sm:items-center gap-2 rounded-full bg-white/90 px-4 py-2 shadow-[0_8px_20px_-10px_rgba(9,13,40,0.3)] ring-1 ring-[#195AFF]/10 backdrop-blur-sm">
                  <Trophy aria-hidden="true" className="h-4 w-4 shrink-0 text-[#f0b429] mt-0.5 sm:mt-0" />
                  {/* flex-col on mobile puts the role on its own second line (it was
                      overflowing/wrapping mid-word in the pill); sm:block returns to the
                      original single-line "Name · Role" layout where there's room for it. */}
                  <p className="m-0 flex flex-col sm:block font-Schibsted_Grotesk text-sm text-[#596278]">
                    <span className="font-['Inter',Arial,sans-serif] font-bold text-[#172342]">
                      {slideIndex === 0 ? "Founder & CEO" : "Pavan Kumar, Marketing Head"}
                    </span>
                    <span className="hidden sm:inline font-['Inter',Arial,sans-serif]">{" "}&middot; </span>
                    <span className="  font-['Inter',Arial,sans-serif]">{slideIndex === 0 ? "Award-Winning Tech Consultant" : "Star Performer of the year"}</span>
                  </p>
                </div>
              </div>


              {/* Each proof point is now its own full card (icon, label, text, background,
                  border, shadow) that fades in and out as one unit — previously only the
                  paragraph text faded while a shared box/border stayed static underneath. */}
              {/* -right-36 (144px) pushed this card well past the viewport edge on mobile,
                  where the photo column has no room to spare — it read as clipped/broken on
                  phones. Only offset it that far once the layout actually has the width for it. */}
              <div className={`absolute z-[2] -right-[110px] sm:-right-16 xl:-right-36 ${slideIndex !== 1 ? "top-[38%]" : "top-[48%]" } sm:top-[43%] w-[min(270px,56%)] min-h-[148px] md:min-h-[132px] sm:w-[min(250px,66%)] transition-opacity duration-500 ${slideIndex === 2 ? "opacity-0 pointer-events-none" : "opacity-100"}`}>
                <div className="absolute inset-0 overflow-hidden rounded-2xl border border-white/70 bg-white/95 px-5 py-4 opacity-0 shadow-[0_28px_54px_-16px_rgba(9,13,40,0.32)] ring-1 ring-[#195AFF]/10 backdrop-blur-sm animate-proof-cycle [animation-fill-mode:backwards]">
                  <div className="mb-2.5 flex items-center gap-2">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#195AFF] to-[#53F6FF] text-white shadow-[0_6px_14px_-4px_rgba(25,90,255,0.6)]">
                      <TrendingUp aria-hidden="true" className="h-3.5 w-3.5" />
                    </span>
                    <span className="font-['Inter',Arial,sans-serif] text-[10px] font-bold uppercase tracking-[0.14em] text-[#8891A7]">Real Impact</span>
                  </div>
                  <p className="m-0 text-[13.5px] font-semibold leading-[1.55] tracking-[-0.1px] text-[#1B2340]">
                    We&apos;ve helped multiple startups achieve <span className="font-semibold font-['Inter',Arial,sans-serif] text-[#195AFF]">₹100+ Cr revenue</span> within 5 years.
                  </p>
                </div>
                <div className="absolute inset-0 overflow-hidden rounded-2xl border border-white/70 bg-white/95 px-5 py-4 opacity-0 shadow-[0_28px_54px_-16px_rgba(9,13,40,0.32)] ring-1 ring-[#195AFF]/10 backdrop-blur-sm animate-proof-cycle [animation-delay:8s] [animation-fill-mode:backwards]">
                  <div className="mb-2.5 flex items-center gap-2">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#195AFF] to-[#53F6FF] text-white shadow-[0_6px_14px_-4px_rgba(25,90,255,0.6)]">
                      <TrendingUp aria-hidden="true" className="h-3.5 w-3.5" />
                    </span>
                    <span className="font-['Inter',Arial,sans-serif] text-[10px] font-bold uppercase tracking-[0.14em] text-[#8891A7]">Real Impact</span>
                  </div>
                  <p className="m-0 text-[13.5px] font-semibold leading-[1.55] tracking-[-0.1px] text-[#1B2340]">
                    In the last year, <span className="font-semibold font-['Inter',Arial,sans-serif] text-[#195AFF]">10+ D2C brands</span> hit their first <span className="font-semibold font-['Inter',Arial,sans-serif] text-[#195AFF]">₹50 Lakhs</span> within 6 months.
                  </p>
                </div>
              </div>
            </div>

            {/* Mobile-only copy of the CTA — the xl:inline-flex version above stays hidden until
                the two-column layout, so this one (below the founder photo/cards) is the only
                one shown before that. */}
            <div className="mt-6 flex flex-col gap-2  xl:hidden">
              <Link href="/book-demo" prefetch={false} className="inline-flex w-full min-h-14 items-center justify-center gap-3 rounded-full bg-[#195AFF] px-7 font-Schibsted_Grotesk text-sm font-semibold text-white transition-colors hover:bg-[#3a6ceb] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-blue-600">
                Book a Free Demo <ArrowRight aria-hidden="true" className="h-4 w-4" />
              </Link>

                <Link href="#core-ecosystem" prefetch={false} className="w-full inline-flex min-h-14 items-center justify-center rounded-full border border-[#b5c8ee] bg-white px-7 font-Schibsted_Grotesk text-sm font-semibold text-[#172342] transition-colors hover:bg-blue-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-blue-600">
                Explore GrowthOS
              </Link>
            </div>
          </div>
        </div>

        </div>
      </div>
        <div className="py-5 my-5">
          <h2 className="mb-5 text-center font-Schibsted_Grotesk text-[10px] font-semibold uppercase tracking-[3px] text-[#73809A]">Banking Partners &amp; Our Clients</h2>
          <BankingPartnersSlider />
        </div>
    </section>
  );
}
